import mapboxgl from 'mapbox-gl';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import type { Coordinate } from '@shared/models/geography.model';
import { getBlueprintCornerOffsets } from '@shared/helpers/design/blueprint-underlay.helpers';
import { convertMetricOffsetToCoordinate } from '@shared/helpers/geometry/coordinates.helpers';
import { applyFloatingOriginMatrix } from '../threejs/floating-origin.helpers';
import { compileShader, linkProgram } from '../threejs/gl-program.helpers';
import { required } from '@shared/helpers/function.helpers';
import { MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID } from '../map.constants';

/**
 * Sit the plan just under the storey floor so extrusion fills (program colours)
 * draw on top after this layer — same readable stack as the ground-floor Mapbox
 * raster. Depth is still written so lower-storey wireframes cannot overpaint the PDF.
 * ({@link map-threejs} extrusion is inserted after the elevated underlay layer.)
 */
const UNDERLAY_Z_BIAS_METERS = 0.025;

export type BlueprintElevatedGlResources = {
  program: WebGLProgram;
  buffer: WebGLBuffer;
  texture: WebGLTexture;
  aPosition: number;
  aUv: number;
  uMatrix: WebGLUniformLocation;
  uOpacity: WebGLUniformLocation;
  uTexture: WebGLUniformLocation;
};

export type BlueprintElevatedLayerState = {
  underlay: BlueprintUnderlay | undefined;
  location: Coordinate | undefined;
  elevationMeters: number;
  textureUrl: string | undefined;
  visible: boolean;
};

/**
 * Compile the textured-quad program used for elevated blueprint underlays.
 */
export const initBlueprintElevatedGl = (
  gl: WebGLRenderingContext,
): BlueprintElevatedGlResources => {
  const vsSource = `
    precision highp float;
    attribute vec3 a_pos;
    attribute vec2 a_uv;
    uniform mat4 u_matrix;
    varying vec2 v_uv;
    void main() {
      gl_Position = u_matrix * vec4(a_pos, 1.0);
      v_uv = a_uv;
    }
  `;
  const fsSource = `
    precision mediump float;
    varying vec2 v_uv;
    uniform sampler2D u_texture;
    uniform float u_opacity;
    void main() {
      vec4 color = texture2D(u_texture, v_uv);
      // Premultiply for Mapbox's canvas blend mode.
      float a = color.a * u_opacity;
      // Skip empty texels so they neither tint nor write depth (opaque plans).
      if (a < 0.01) discard;
      gl_FragColor = vec4(color.rgb * a, a);
    }
  `;

  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSource);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSource);
  const program = linkProgram(gl, vs, fs);
  const buffer = gl.createBuffer();
  const texture = gl.createTexture();
  if (!buffer || !texture) {
    gl.deleteProgram(program);
    if (buffer) gl.deleteBuffer(buffer);
    if (texture) gl.deleteTexture(texture);
    throw new Error(
      'Failed to create elevated blueprint underlay GL resources',
    );
  }

  const aPosition = gl.getAttribLocation(program, 'a_pos');
  const aUv = gl.getAttribLocation(program, 'a_uv');
  const uMatrix = gl.getUniformLocation(program, 'u_matrix');
  const uOpacity = gl.getUniformLocation(program, 'u_opacity');
  const uTexture = gl.getUniformLocation(program, 'u_texture');
  if (aPosition < 0 || aUv < 0 || !uMatrix || !uOpacity || !uTexture) {
    gl.deleteBuffer(buffer);
    gl.deleteTexture(texture);
    gl.deleteProgram(program);
    throw new Error(
      'Failed to resolve elevated blueprint underlay shader locations',
    );
  }

  // Neutral placeholder so first draws before the async image loads are invisible.
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    1,
    1,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    new Uint8Array([0, 0, 0, 0]),
  );

  return {
    program,
    buffer,
    texture,
    aPosition,
    aUv,
    uMatrix,
    uOpacity,
    uTexture,
  };
};

/**
 * Dispose elevated underlay GL resources.
 */
export const destroyBlueprintElevatedGl = (
  gl: WebGLRenderingContext,
  resources: BlueprintElevatedGlResources,
): void => {
  gl.deleteBuffer(resources.buffer);
  gl.deleteTexture(resources.texture);
  gl.deleteProgram(resources.program);
};

/**
 * Upload a data-URL raster into the elevated underlay texture.
 */
export const loadBlueprintElevatedTexture = (
  gl: WebGLRenderingContext,
  texture: WebGLTexture,
  imageDataUrl: string,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);
        gl.texImage2D(
          gl.TEXTURE_2D,
          0,
          gl.RGBA,
          gl.RGBA,
          gl.UNSIGNED_BYTE,
          image,
        );
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 0);
        resolve();
      } catch (error: unknown) {
        reject(error);
      }
    };
    image.onerror = () =>
      reject(new Error('Failed to decode blueprint underlay image'));
    image.src = imageDataUrl;
  });

/**
 * Build a mercator-relative textured quad (two triangles) for the underlay plane.
 * Vertex layout: pos(3) + uv(2) = 5 floats.
 */
export const buildBlueprintElevatedVertices = (
  underlay: BlueprintUnderlay,
  location: Coordinate,
  elevationMeters: number,
): Float32Array => {
  const originMercator = mapboxgl.MercatorCoordinate.fromLngLat(location, 0);
  const zMeters = elevationMeters - UNDERLAY_Z_BIAS_METERS;
  const corners = getBlueprintCornerOffsets(underlay);
  // UV with FLIP_Y upload: image top → v=1.
  const uvs: ReadonlyArray<readonly [number, number]> = [
    [0, 1],
    [1, 1],
    [1, 0],
    [0, 0],
  ];

  const positions = corners.map((offset) => {
    const { lng, lat } = convertMetricOffsetToCoordinate(location, offset);
    const merc = mapboxgl.MercatorCoordinate.fromLngLat({ lng, lat }, zMeters);
    return {
      x: merc.x - originMercator.x,
      y: merc.y - originMercator.y,
      z: merc.z - originMercator.z,
    };
  });

  // Triangles 0-1-2 and 0-2-3 (same winding as Mapbox image corners).
  const order = [0, 1, 2, 0, 2, 3] as const;
  const out = new Float32Array(order.length * 5);
  let cursor = 0;
  for (const index of order) {
    const pos = required(positions[index], 'Missing elevated underlay vertex');
    const uv = required(uvs[index], 'Missing elevated underlay UV');
    out[cursor++] = pos.x;
    out[cursor++] = pos.y;
    out[cursor++] = pos.z;
    out[cursor++] = uv[0];
    out[cursor++] = uv[1];
  }
  return out;
};

/**
 * Draw the elevated underlay quad for the current frame.
 */
export const renderBlueprintElevatedLayer = (
  gl: WebGLRenderingContext,
  matrix: number[],
  resources: BlueprintElevatedGlResources,
  state: BlueprintElevatedLayerState,
  origin: mapboxgl.MercatorCoordinate,
): void => {
  if (!state.visible || !state.underlay || !state.location) return;

  const vertices = buildBlueprintElevatedVertices(
    state.underlay,
    state.location,
    state.elevationMeters,
  );

  gl.bindBuffer(gl.ARRAY_BUFFER, resources.buffer);
  gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.DYNAMIC_DRAW);

  gl.useProgram(resources.program);
  const stride = 5 * 4;
  gl.enableVertexAttribArray(resources.aPosition);
  gl.vertexAttribPointer(resources.aPosition, 3, gl.FLOAT, false, stride, 0);
  gl.enableVertexAttribArray(resources.aUv);
  gl.vertexAttribPointer(resources.aUv, 2, gl.FLOAT, false, stride, 3 * 4);

  gl.uniformMatrix4fv(
    resources.uMatrix,
    false,
    applyFloatingOriginMatrix(matrix, origin),
  );
  gl.uniform1f(resources.uOpacity, state.underlay.opacity);

  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, resources.texture);
  gl.uniform1i(resources.uTexture, 0);

  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
  gl.enable(gl.DEPTH_TEST);
  gl.depthFunc(gl.LEQUAL);
  // Write depth so later geometry below the plan cannot overpaint the PDF.
  // Same-storey fills sit slightly above this plane and still composite on top.
  // Fills for lower storeys are also skipped in MapThreeJs when underlay is on.
  gl.depthMask(true);
  gl.disable(gl.CULL_FACE);

  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.depthMask(true);
};

/** Mapbox custom-layer id for the elevated underlay plane. */
export const BLUEPRINT_ELEVATED_LAYER_ID = MAPBOX_BLUEPRINT_ELEVATED_LAYER_ID;

/**
 * True when the underlay should use the elevated custom layer instead of the
 * ground-draped Mapbox image source.
 *
 * Always elevated: the raster path is locked to map ground (0), so a storey-1
 * floor with negative elevation would leave the PDF floating above the slab.
 * The custom plane follows {@link elevationMeters} for 0, below-grade, and upper floors.
 */
export const shouldUseElevatedBlueprintUnderlay = (
  _elevationMeters: number,
): boolean => true;
